import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Coffee, Target, Heart, Briefcase, ArrowRight, Mail, Linkedin, Twitter, Github, X, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import siteLogo from '../assets/logo.png';
import { getActiveJobs, getSettings, submitApplication } from '../services/api';
import { validateFile, RESUME_EXTENSIONS } from '../utils/fileValidation';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

export default function Careers({ embedded = false }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState('default');
  const [openPositions, setOpenPositions] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [applyJob, setApplyJob] = useState(null); // job being applied to
  const [appForm, setAppForm] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [appSubmitting, setAppSubmitting] = useState(false);
  const [appSuccess, setAppSuccess] = useState(false);
  const [appError, setAppError] = useState('');

  useEffect(() => {
    const handleMouseMove = (e) => setMousePosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    getActiveJobs()
      .then((jobs) => setOpenPositions(Array.isArray(jobs) ? jobs : []))
      .catch(() => setOpenPositions([]))
      .finally(() => setJobsLoading(false));
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const lifeAtCodinity = [
    { icon: <Users className="w-8 h-8" />, title: 'Collaborative Culture', description: 'Work with talented individuals in a supportive, team-oriented environment where ideas are valued and innovation thrives.' },
    { icon: <Coffee className="w-8 h-8" />, title: 'Work-Life Balance', description: 'Flexible hours, remote options, and generous time-off policies ensure you can maintain a healthy work-life balance.' },
    { icon: <Target className="w-8 h-8" />, title: 'Growth Opportunities', description: 'Continuous learning programs, mentorship, and clear career advancement paths to help you reach your full potential.' },
    { icon: <Heart className="w-8 h-8" />, title: 'Health & Wellness', description: 'Comprehensive health benefits, wellness programs, and mental health support for all team members.' },
  ];

  const values = [
    { title: 'Innovation First', description: 'We embrace new technologies and encourage creative problem-solving.' },
    { title: 'Collaboration Over Competition', description: 'We win together as a team, supporting each other\'s growth and success.' },
    { title: 'Quality Matters', description: 'We take pride in our work and maintain high standards in everything we do.' },
    { title: 'Continuous Learning', description: 'We invest in our team\'s growth through education and skill development.' },
    { title: 'Work-Life Harmony', description: 'We believe in sustainable productivity and respecting personal time.' },
    { title: 'Diversity & Inclusion', description: 'We celebrate different perspectives and create an inclusive environment.' },
  ];

  const careersEmail = settings?.company?.careersEmail || 'careers@codinity.com';
  const linkedin = settings?.social?.linkedin || 'https://linkedin.com/company/codinity';
  const github = settings?.social?.github || 'https://github.com/codinity';
  const twitter = settings?.social?.twitter || '#';
  const heroTitle = settings?.careers?.heroTitle || 'Build Your Career';
  const heroSubtitle = settings?.careers?.heroSubtitle || 'With Codinity';
  const heroDescription = settings?.careers?.heroDescription || 'Join a team of passionate innovators building cutting-edge solutions.';

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Application Modal ──────────────────────────────────────────────────────

  const openApplyModal = (job) => {
    setApplyJob(job);
    setAppForm({
      fullName: '', email: '', phone: '', location: '',
      linkedinUrl: '', portfolioUrl: '',
      yearsOfExperience: '', currentJobTitle: '', currentEmployer: '',
      lastDrawnSalaryAmount: '', lastDrawnSalaryCurrency: 'USD',
      expectedSalaryAmount: '', expectedSalaryCurrency: 'USD',
      noticePeriod: '',
      coverLetter: '',
      problemSolvingAnswer: '',
      howDidYouHear: 'Company Website',
      workAuthorization: '', willingToRelocate: 'false',
      additionalInfo: '',
    });
    setResumeFile(null);
    setAppSuccess(false);
    setAppError('');
  };

  const closeApplyModal = () => { setApplyJob(null); setAppSuccess(false); };

  const handleAppChange = (e) => {
    const { name, value } = e.target;
    setAppForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAppSubmit = async (e) => {
    e.preventDefault();
    if (!resumeFile) { setAppError('Please upload your resume.'); return; }
    const { valid, error } = validateFile(resumeFile, RESUME_EXTENSIONS);
    if (!valid) { setAppError(error); return; }
    setAppSubmitting(true);
    setAppError('');
    try {
      // 1. Upload resume directly to Cloudinary (unsigned preset — no API key needed)
      const cloudData = await uploadToCloudinary(resumeFile, 'codinity/resumes');

      // 2. Submit application with the Cloudinary URL
      await submitApplication({
        jobId: applyJob._id,
        ...appForm,
        resumeUrl: cloudData.secure_url,
        resumeOriginalName: resumeFile.name,
      });
      setAppSuccess(true);
    } catch (err) {
      setAppError(err.response?.data?.error || err.message || 'Submission failed. Please try again or email us directly.');
    } finally {
      setAppSubmitting(false);
    }
  };

  const inputCls = 'w-full bg-slate-800/80 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all';
  const labelCls = 'block text-xs font-semibold text-slate-400 mb-1 uppercase tracking-wide';
  const selectCls = `${inputCls} cursor-pointer`;

  const sectionSnapClass = 'snap-start min-h-screen relative';

  return (
    <div className={`min-h-screen bg-slate-950 text-white overflow-hidden ${embedded ? '' : 'cursor-none'}`}>

      {/* Custom Cursor */}
      {!embedded && (
        <>
          <div className="hidden lg:block fixed w-8 h-8 pointer-events-none z-[9999] mix-blend-difference" style={{ left: `${mousePosition.x}px`, top: `${mousePosition.y}px`, transform: 'translate(-50%, -50%)' }}>
            <div className={`w-full h-full rounded-full border-2 border-cyan-400 ${cursorVariant === 'hover' ? 'scale-150' : 'scale-100'} transition-transform duration-200`}></div>
          </div>
          <div className="hidden lg:block fixed w-2 h-2 bg-cyan-400 rounded-full pointer-events-none z-[9998]" style={{ left: `${mousePosition.x}px`, top: `${mousePosition.y}px`, transform: 'translate(-50%, -50%)' }}></div>
        </>
      )}

      {/* Header */}
      {!embedded && (
        <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/95 backdrop-blur-lg shadow-lg shadow-blue-500/5 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
            <Link to="/" className="flex items-center no-underline">
              <img src={siteLogo} alt="Codinity" style={{ height: '96px', width: 'auto' }} className="object-contain" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium no-underline">Home</Link>
              <Link to="/careers" onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-blue-400 transition-colors duration-300 font-medium no-underline">Careers</Link>
              <Link to="/dashboard" onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium no-underline">Dashboard</Link>
              <button onClick={() => scrollToSection('contact')} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium bg-transparent border-none cursor-pointer">Contact</button>
            </div>
          </div>
        </header>
      )}

      {/* Hero Section */}
      <section className={`${sectionSnapClass} flex items-center justify-center overflow-hidden pb-40 ${embedded ? 'pt-0' : 'pt-32'}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8 animate-fade-in">
            <div className="inline-block">
              <span className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-semibold backdrop-blur-sm">
                <Briefcase className="w-4 h-4 inline mr-2" /> Join Our Team
              </span>
            </div>
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold leading-tight">
              {heroTitle}
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">{heroSubtitle}</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">{heroDescription}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <button onClick={() => scrollToSection('open-positions')} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl shadow-blue-500/25 flex items-center gap-2 border-none cursor-pointer text-white">
                View Open Positions <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-24 bg-slate-950 relative snap-start min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6">What We Believe In</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div key={index} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="bg-white/5 backdrop-blur-sm border border-blue-500/20 hover:border-blue-400/50 rounded-2xl p-6 transition-all duration-300 cursor-pointer hover:bg-white/8">
                <h3 className="text-xl font-bold mb-3 text-blue-400">{value.title}</h3>
                <p className="text-slate-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diverse Team Image */}
      <section className="relative h-screen overflow-hidden snap-start">
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-300" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=1080&fit=crop)', backgroundAttachment: 'fixed' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/40"></div>
        </div>
      </section>

      {/* Life at Codinity */}
      <section className="relative snap-start min-h-screen flex items-center py-24" style={{ background: 'linear-gradient(to bottom, #020817 0%, #0a1628 20%, #0c1e40 40%, #162d7a 50%, #0c1e40 60%, #0a1628 80%, #020817 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(26,58,138,0.30) 0%, transparent 70%)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Life at Codinity</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">Where Innovation Meets Culture</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">Experience a workplace that values creativity, growth, and work-life balance while building amazing products.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lifeAtCodinity.map((item, index) => (
              <div key={index} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="group bg-slate-900 border border-slate-700 hover:border-blue-500/60 rounded-2xl p-6 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer shadow-lg shadow-black/40">
                <div className="w-14 h-14 bg-blue-500/15 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <div className="text-blue-400">{item.icon}</div>
                </div>
                <h3 className="text-lg font-bold mb-2 text-white group-hover:text-blue-400 transition-colors">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="open-positions" className="py-24 bg-slate-900/30 relative snap-start min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Open Positions</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6">Join Our Team</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">We're always looking for talented individuals who are passionate about technology and innovation.</p>
          </div>

          {jobsLoading ? (
            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto" aria-label="Loading open positions">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6">
                  <div className="skeleton-shimmer h-6 w-2/3 mb-3"></div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    <div className="skeleton-shimmer h-7 w-24 rounded-full"></div>
                    <div className="skeleton-shimmer h-7 w-20 rounded-full"></div>
                    <div className="skeleton-shimmer h-7 w-20 rounded-full"></div>
                  </div>
                  <div className="skeleton-shimmer h-4 w-full mb-2"></div>
                  <div className="skeleton-shimmer h-4 w-5/6 mb-5"></div>
                  <div className="space-y-2 mb-6">
                    <div className="skeleton-shimmer h-3.5 w-3/4"></div>
                    <div className="skeleton-shimmer h-3.5 w-2/3"></div>
                    <div className="skeleton-shimmer h-3.5 w-4/5"></div>
                  </div>
                  <div className="skeleton-shimmer h-12 w-full rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : openPositions.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No open positions at the moment.</p>
              <p className="text-sm mt-2">Check back soon or send your resume below.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              {openPositions.map((position) => (
                <div key={position._id} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="group bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-blue-500/50 rounded-2xl p-6 transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold mb-2 group-hover:text-blue-400 transition-colors">{position.title}</h3>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full">{position.department}</span>
                        <span className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full">📍 {position.location}</span>
                        <span className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full">{position.type}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-400 mb-4">{position.description}</p>
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-2">Requirements:</h4>
                    <ul className="space-y-1">
                      {position.requirements.map((req, i) => (
                        <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                          <span className="text-blue-400 mt-1">•</span><span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => openApplyModal(position)}
                    className="w-full group-hover:bg-blue-500 bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 border-none cursor-pointer"
                  >
                    Apply Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <p className="text-slate-400 mb-6">Don't see a position that fits?</p>
            <button onClick={() => scrollToSection('contact')} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="px-8 py-3 border-2 border-blue-500 text-blue-400 rounded-lg font-semibold hover:bg-blue-500 hover:text-white transition-all duration-300 bg-transparent cursor-pointer">
              Send Us Your Resume
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 pb-24 mb-16 relative snap-start">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%)] bg-[length:250%_250%]"></div>
            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-4">Ready to Join Our Team?</h2>
              <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">We'd love to hear from you! Send us your resume and let's start the conversation.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 max-w-md mx-auto">
                <a href={`mailto:${careersEmail}`} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-slate-100 px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl no-underline cursor-pointer">
                  <Mail className="w-5 h-5" />{careersEmail}
                </a>
              </div>
              <div className="mt-8 pt-8 border-t border-white/20">
                <div className="font-semibold mb-4">Follow Us</div>
                <div className="flex gap-4 justify-center">
                  {[
                    { icon: <Linkedin className="w-5 h-5" />, href: linkedin },
                    twitter && twitter !== '#' && { icon: <Twitter className="w-5 h-5" />, href: twitter },
                    { icon: <Github className="w-5 h-5" />, href: github },
                  ].filter(Boolean).map((social, index) => (
                    <a key={index} href={social.href} target="_blank" rel="noopener noreferrer" onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="w-12 h-12 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg flex items-center justify-center transition-all duration-300 hover:transform hover:scale-110 no-underline text-white">
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {!embedded && (
        <footer className="bg-slate-900/50 border-t border-slate-800 py-6 snap-start">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <img src={siteLogo} alt="Codinity" style={{ height: '72px', width: 'auto' }} className="object-contain" />
              <div className="flex gap-6 text-sm">
                <Link to="/" onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-400 hover:text-blue-400 transition-colors no-underline">Home</Link>
                <button onClick={() => scrollToSection('open-positions')} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-400 hover:text-blue-400 transition-colors cursor-pointer bg-transparent border-none p-0">Open Positions</button>
                <button onClick={() => scrollToSection('contact')} onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-400 hover:text-blue-400 transition-colors cursor-pointer bg-transparent border-none p-0">Contact</button>
                <Link to="/dashboard" onMouseEnter={() => setCursorVariant('hover')} onMouseLeave={() => setCursorVariant('default')} className="text-slate-400 hover:text-blue-400 transition-colors no-underline">Dashboard</Link>
              </div>
              <p className="text-slate-400 text-sm">© {new Date().getFullYear()} {settings?.company?.name || 'Codinity'}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}

      {/* ─── Job Application Modal ────────────────────────────────────────────── */}
      {applyJob && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={closeApplyModal} />
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">

            {/* Modal Header */}
            <div className="flex items-start justify-between p-5 border-b border-slate-800 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-white">Apply for {applyJob.title}</h2>
                <p className="text-sm text-blue-400 mt-0.5">{applyJob.department} · {applyJob.location} · {applyJob.type}</p>
              </div>
              <button onClick={closeApplyModal} className="text-slate-400 hover:text-white transition-colors p-1">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 p-5">
              {appSuccess ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Application Submitted!</h3>
                  <p className="text-slate-400 max-w-sm mx-auto">Thanks for applying! We'll review your application and get back to you.</p>
                  <button onClick={closeApplyModal} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors border-none cursor-pointer">
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAppSubmit} className="space-y-6">

                  {/* Personal Information */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-3 pb-1 border-b border-slate-800">Personal Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Full Name *</label>
                        <input name="fullName" required className={inputCls} value={appForm.fullName || ''} onChange={handleAppChange} placeholder="John Smith" />
                      </div>
                      <div>
                        <label className={labelCls}>Email Address *</label>
                        <input name="email" type="email" required className={inputCls} value={appForm.email || ''} onChange={handleAppChange} placeholder="john@example.com" />
                      </div>
                      <div>
                        <label className={labelCls}>Phone Number *</label>
                        <input name="phone" required className={inputCls} value={appForm.phone || ''} onChange={handleAppChange} placeholder="+1 234 567 8900" />
                      </div>
                      <div>
                        <label className={labelCls}>Location (City, Country) *</label>
                        <input name="location" required className={inputCls} value={appForm.location || ''} onChange={handleAppChange} placeholder="New York, USA" />
                      </div>
                      <div>
                        <label className={labelCls}>LinkedIn Profile</label>
                        <input name="linkedinUrl" type="url" className={inputCls} value={appForm.linkedinUrl || ''} onChange={handleAppChange} placeholder="https://linkedin.com/in/..." />
                      </div>
                      <div>
                        <label className={labelCls}>Portfolio / GitHub</label>
                        <input name="portfolioUrl" type="url" className={inputCls} value={appForm.portfolioUrl || ''} onChange={handleAppChange} placeholder="https://github.com/..." />
                      </div>
                    </div>
                  </div>

                  {/* Professional Background */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-3 pb-1 border-b border-slate-800">Professional Background</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Years of Experience *</label>
                        <input name="yearsOfExperience" type="number" min="0" max="50" required className={inputCls} value={appForm.yearsOfExperience || ''} onChange={handleAppChange} placeholder="5" />
                      </div>
                      <div>
                        <label className={labelCls}>Notice Period *</label>
                        <select name="noticePeriod" required className={selectCls} value={appForm.noticePeriod || ''} onChange={handleAppChange}>
                          <option value="">Select...</option>
                          <option>Immediate</option>
                          <option>2 weeks</option>
                          <option>1 month</option>
                          <option>2 months</option>
                          <option>3 months+</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Current / Most Recent Job Title</label>
                        <input name="currentJobTitle" className={inputCls} value={appForm.currentJobTitle || ''} onChange={handleAppChange} placeholder="Senior Developer" />
                      </div>
                      <div>
                        <label className={labelCls}>Current / Recent Employer</label>
                        <input name="currentEmployer" className={inputCls} value={appForm.currentEmployer || ''} onChange={handleAppChange} placeholder="Company Name" />
                      </div>
                      <div>
                        <label className={labelCls}>Last Drawn Salary</label>
                        <div className="flex gap-2">
                          <select name="lastDrawnSalaryCurrency" className={`${selectCls} w-24 flex-shrink-0`} value={appForm.lastDrawnSalaryCurrency || 'USD'} onChange={handleAppChange}>
                            <option>USD</option><option>EUR</option><option>GBP</option><option>PKR</option><option>INR</option><option>AED</option><option>SAR</option>
                          </select>
                          <input name="lastDrawnSalaryAmount" type="number" min="0" className={inputCls} value={appForm.lastDrawnSalaryAmount || ''} onChange={handleAppChange} placeholder="e.g. 80000" />
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Expected Salary *</label>
                        <div className="flex gap-2">
                          <select name="expectedSalaryCurrency" className={`${selectCls} w-24 flex-shrink-0`} value={appForm.expectedSalaryCurrency || 'USD'} onChange={handleAppChange}>
                            <option>USD</option><option>EUR</option><option>GBP</option><option>PKR</option><option>INR</option><option>AED</option><option>SAR</option>
                          </select>
                          <input name="expectedSalaryAmount" type="number" min="0" required className={inputCls} value={appForm.expectedSalaryAmount || ''} onChange={handleAppChange} placeholder="e.g. 100000" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Application Materials */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-3 pb-1 border-b border-slate-800">Application Materials</h3>
                    <div className="space-y-3">
                      {/* Resume Upload */}
                      <div>
                        <label className={labelCls}>Resume / CV * (PDF or Word, max 5MB)</label>
                        <label className="flex items-center gap-3 bg-slate-800/80 border border-dashed border-slate-600 hover:border-blue-500 rounded-lg px-4 py-4 cursor-pointer transition-colors group">
                          <Upload size={20} className="text-slate-500 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            {resumeFile ? (
                              <span className="text-sm text-green-400 truncate">{resumeFile.name}</span>
                            ) : (
                              <span className="text-sm text-slate-500">Click to upload resume (PDF, DOC, DOCX)</span>
                            )}
                          </div>
                          <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => {
                            const file = e.target.files[0] || null;
                            e.target.value = '';
                            if (!file) return;
                            const { valid, error } = validateFile(file, RESUME_EXTENSIONS);
                            if (!valid) { setResumeFile(null); setAppError(error); return; }
                            setResumeFile(file);
                            setAppError('');
                          }} />
                        </label>
                      </div>
                      {/* Cover Letter */}
                      <div>
                        <label className={labelCls}>Cover Letter *</label>
                        <textarea name="coverLetter" required rows={4} className={`${inputCls} resize-none`} value={appForm.coverLetter || ''} onChange={handleAppChange} placeholder="Tell us why you're a great fit for this role and what makes you stand out..." />
                      </div>
                    </div>
                  </div>

                  {/* Screening Question */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-3 pb-1 border-b border-slate-800">Screening Question</h3>
                    <div>
                      <label className={labelCls}>
                        Describe a challenging technical problem you faced in your career where you felt completely stuck. What was the problem, how did you approach solving it, and what did you learn from the experience? *
                      </label>
                      <textarea name="problemSolvingAnswer" required rows={6} minLength={150} className={`${inputCls} resize-none`} value={appForm.problemSolvingAnswer || ''} onChange={handleAppChange} placeholder="Be specific, describe the problem, your thought process, the solution you found, and what you'd do differently now. (min 150 characters)" />
                      <p className="text-xs text-slate-600 mt-1">{(appForm.problemSolvingAnswer || '').length} / 150 min characters</p>
                    </div>
                  </div>

                  {/* Additional */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-3 pb-1 border-b border-slate-800">Additional Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>How did you hear about this position? *</label>
                        <select name="howDidYouHear" required className={selectCls} value={appForm.howDidYouHear || ''} onChange={handleAppChange}>
                          <option value="Company Website">Company Website</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="GitHub">GitHub</option>
                          <option value="Referral">Referral</option>
                          <option value="Job Board">Job Board</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Willing to Relocate?</label>
                        <select name="willingToRelocate" className={selectCls} value={appForm.willingToRelocate || 'false'} onChange={handleAppChange}>
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Are you authorized to work in the position's location? *</label>
                        <select name="workAuthorization" required className={selectCls} value={appForm.workAuthorization || ''} onChange={handleAppChange}>
                          <option value="">Select...</option>
                          <option value="true">Yes, I am authorized</option>
                          <option value="false">No, I would require sponsorship</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Anything else you'd like us to know?</label>
                        <textarea name="additionalInfo" rows={3} className={`${inputCls} resize-none`} value={appForm.additionalInfo || ''} onChange={handleAppChange} placeholder="Any additional context, special circumstances, or anything else relevant..." />
                      </div>
                    </div>
                  </div>

                  {/* Error */}
                  {appError && (
                    <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400">
                      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                      {appError}
                    </div>
                  )}

                  {/* Submit */}
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={closeApplyModal} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 px-6 py-3 rounded-lg font-medium transition-colors border-none cursor-pointer">
                      Cancel
                    </button>
                    <button type="submit" disabled={appSubmitting} className="flex-2 flex-grow-[2] bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors border-none cursor-pointer flex items-center justify-center gap-2">
                      {appSubmitting ? 'Submitting...' : 'Submit Application'}
                      {!appSubmitting && <ArrowRight size={16} />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
